import { PortalRequestsPermission } from '@idc-forms/permissions'
import { CalendarClock, ClipboardCheck, Link2, RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router'
import { PATHS, SEGMENTS } from '@/app/router/routes'
import { useClientPortalFlows } from '@/features/client-portal/hooks/use-portal-admin'
import { usePortalLink } from '@/features/client-portal/hooks/use-portal-link'
import { Can } from '@/shared/components/can'
import { Button } from '@/shared/ui/button'
import { validationTypeLabel } from '../consts/portal-flow-labels'
import {
  formatFlowDate,
  SHAREABLE_FLOW_STATUSES,
  selectCurrentFlow
} from '../utils/portal-flow-selection'
import { PortalStatusBadge } from './portal-status-badge'

// Estados desde los que el admin puede revisar (aprobar / pedir correcciones).
const REVIEWABLE = new Set(['submitted', 'resubmitted', 'under_review'])

interface ActiveFlowPanelProps {
  clientId: string
  clientType: 'individual' | 'juridical'
  /** Fallback cuando el cliente no tiene NINGÚN flujo (ni activo ni expirado). */
  renderEmpty?: () => React.ReactNode
}

/**
 * Panel del flujo activo del portal (diseño SIDEBAR-4/6): estado, tipo de validación,
 * "Revisar expediente" (abre la revisión por campo cuando el cliente ya envió) y
 * copiar enlace. Obtiene el flujo (id/status) del hook por cliente.
 */
export function ActiveFlowPanel({ clientId, clientType, renderEmpty }: ActiveFlowPanelProps) {
  const navigate = useNavigate()
  const { flows, isLoading, extendLink } = useClientPortalFlows(clientId)

  // El flujo activo = el más reciente no terminal. Si no hay, el expirado más reciente
  // (para poder renovarlo) en lugar de "no se encontró flujo".
  const flow = selectCurrentFlow(flows ?? [])

  const goReview = () => {
    // Drill-in de revisión por campo dentro del hub del portal (`/revisar`): se siente
    // parte del expediente. El router es compartido y agnóstico al tipo de persona; cada
    // tipo tiene su propia ruta `validate`.
    const base =
      clientType === 'juridical'
        ? PATHS.admin.clients.juridical.validate(clientId)
        : PATHS.admin.clients.individual.validate(clientId)
    navigate(`${base}/${SEGMENTS.portal.revisar}`)
  }

  // ☠️ La preocupación que este panel tenía escrita ahora es IMPOSIBLE, no sólo evitada.
  //
  // Decía: «un magic link es una credencial; el listado v1 mandaba uno por fila, así que una tabla
  // de 436 clientes bajaba al navegador 436 enlaces vivos para poder copiar uno». Era cierto, y la
  // mitigación era pedir sólo el del expediente abierto — o sea, disciplina.
  //
  // v2 no la necesita: la credencial **no viaja en ninguna respuesta de listado ni de detalle**. Se
  // pide de a una, a un endpoint que la audita. Ya no hay forma de bajar 436 aunque alguien quiera.
  const { copyLink, isCopying } = usePortalLink()

  if (isLoading) {
    return <p className="text-xs text-muted-foreground">Cargando flujo…</p>
  }

  if (!flow) {
    // Ningún flujo (ni activo ni expirado) → el fallback decide (p.ej. crear flujo).
    return renderEmpty ? (
      <>{renderEmpty()}</>
    ) : (
      <p className="text-xs text-muted-foreground">No se encontró el flujo activo.</p>
    )
  }

  const canReview = REVIEWABLE.has(flow.status)
  // El cliente está llenando: el asesor puede entrar a ver el expediente en curso y, desde
  // el shell, traerlo a revisión (tomar la pelota).
  const isFilling = flow.status === 'in_progress'
  const isExpired = flow.status === 'expired'
  const reminderCount = flow.reminderCount ?? 0

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase text-muted-foreground">
          {isExpired ? 'Último flujo' : 'Flujo activo'}
        </span>
        <PortalStatusBadge status={flow.status} />
      </div>

      <div className="text-xs text-muted-foreground">
        Tipo de validación: {validationTypeLabel(flow.validationType)}
      </div>

      {/* Gestión de recordatorios y expiración (PRD §8/§9). */}
      <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <CalendarClock className="h-3.5 w-3.5" />
          {isExpired ? 'Expiró el ' : 'Expira el '}
          {formatFlowDate(flow.expiresAt)}
        </div>
        <div className="mt-1">
          {reminderCount === 0
            ? 'Sin recordatorios enviados'
            : `${reminderCount} recordatorio(s) · último ${formatFlowDate(flow.lastReminderAt)}`}
        </div>
      </div>

      {/* Renovar el enlace cuando expiró (vuelve a enviarse con nueva ventana). */}
      {isExpired && (
        <Can permission={PortalRequestsPermission.ExtendPortalLink}>
          <Button
            type="button"
            className="w-full gap-2"
            onClick={() => extendLink.mutate({ id: flow.id })}
            disabled={extendLink.isPending}
          >
            <RefreshCw className="h-4 w-4" />
            {extendLink.isPending ? 'Renovando…' : 'Renovar enlace'}
          </Button>
        </Can>
      )}

      {/* Revisión por campo: cuando el cliente ya envió, o entrar a ver lo que lleva en curso
          (in_progress) para poder traerlo a revisión desde el shell. */}
      {canReview ? (
        <Button type="button" className="w-full gap-2" onClick={goReview}>
          <ClipboardCheck className="h-4 w-4" />
          Revisar expediente
        </Button>
      ) : isFilling ? (
        <Button type="button" variant="outline" className="w-full gap-2" onClick={goReview}>
          <ClipboardCheck className="h-4 w-4" />
          Ver expediente en curso
        </Button>
      ) : (
        !isExpired && (
          <p className="text-xs text-muted-foreground">
            El cliente aún no ha enviado su información. Podrás revisarla cuando la envíe.
          </p>
        )
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full justify-start gap-2"
        onClick={() => {
          if (flow) copyLink(flow.id)
        }}
        disabled={!flow || !SHAREABLE_FLOW_STATUSES.has(flow.status) || isCopying}
      >
        <Link2 className="h-4 w-4" />
        Copiar enlace del formulario
      </Button>
    </section>
  )
}
